import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type { OnboardingIntent, Persona } from "@/models/onboardingPersona";
import type {
  ActionError,
  PurchaseCustomerInfo,
  PurchasePackage,
  RemoteConfig,
  SubscriptionManagementProvider,
  Transcript,
  User,
} from "@/types";

export type FeatureKey =
  | "transcribe"
  | "translate"
  | "summary"
  | "quiz"
  | "flashcard"
  | "dictation";

export type FeatureFirstUsedAt = {
  transcribe?: number;
  translate?: number;
  summary?: number;
  quiz?: number;
  flashcard?: number;
  dictation?: number;
};

export type WorkHealth = {
  startedAt: number;
  lastProgressAt: number;
  finishedAt?: number;
  status: "running" | "completed" | "failed" | "cancelled";
  error?: ActionError;
};

export type AppState = {
  _hasHydrated: boolean;
  _hydrationError: boolean;
  _sessionExpiredAt: number | null;
  userToken?: string;
  deviceId?: string;
  loginPromptDismissed?: boolean;
  globalDictationOnboardingDismissed?: boolean;
  globalDictationIntroCompleted?: boolean;
  globalDictationAccessibilityDisclosureAccepted?: boolean;
  selectedTagFilter: string | null;
  webSubscriptionPlans: RemoteConfig["webPlans"];
  country?: string;
  guestTranscript?: Transcript;
  micPermissionGranted?: boolean;
  notificationPermissionRequested?: boolean;
  showLoginBanner?: boolean;
  isSubscribed?: boolean;
  subscriptionManagementProvider?: SubscriptionManagementProvider;
  nativePurchase?: {
    packages: PurchasePackage[];
    customerInfo: PurchaseCustomerInfo;
  };
  remoteConfig?: RemoteConfig;
  userData?: Partial<User>;
  transcriptsNextCursor?: string;
  transcripts: Transcript[];
  workHealth: Record<string, WorkHealth>;
  hasCompletedOnboarding: boolean;
  ratedTranscriptIds: string[];
  postTranscriptionReviewRequested?: boolean;
  firstLaunchAt: number | null;
  dictationBubbleOpenedAt?: number;
  lastAppOpenAt: number | null;
  lastTranscriptCreatedAt: number | null;
  featureFirstUsedAt: FeatureFirstUsedAt;
  onboardingPersona: Persona | null;
  onboardingUseCases: string[];
  onboardingIntent: OnboardingIntent | null;
  // Client-only custom find→replace vocabulary. Composed into native rules
  // on configure.
  dictationVocabulary?: {
    entries: Array<{ from: string; to: string }>;
  };
  // Last custom instructions per AI feature — prefills the regenerate
  // prompt so repeat customizations are one tap instead of retyping.
  lastAiInstructions?: Partial<
    Record<"summary" | "quiz" | "flashcard", string>
  >;
};

export const defaultWebSubscriptionPlans: RemoteConfig["webPlans"] = {
  monthly: "mid_tier_monthly",
  annual: "mid_tier_annual",
};

export const normalizeWebSubscriptionPlans = (
  plans?: Partial<RemoteConfig["webPlans"]>,
): RemoteConfig["webPlans"] => ({
  monthly: plans?.monthly || defaultWebSubscriptionPlans.monthly,
  annual: plans?.annual || defaultWebSubscriptionPlans.annual,
});

export const selectLatestUsableTranscriptId = (
  transcripts: Transcript[] | undefined | null,
): string | null =>
  transcripts?.find(
    (transcript) => transcript.status === "transcription_completed",
  )?.id ?? null;

export const initialState: AppState = {
  _hasHydrated: false,
  _hydrationError: false,
  _sessionExpiredAt: null,
  transcripts: [],
  workHealth: {},
  webSubscriptionPlans: defaultWebSubscriptionPlans,
  selectedTagFilter: null,
  showLoginBanner: true,
  userData: undefined,
  hasCompletedOnboarding: false,
  ratedTranscriptIds: [],
  firstLaunchAt: null,
  lastAppOpenAt: null,
  lastTranscriptCreatedAt: null,
  featureFirstUsedAt: {},
  onboardingPersona: null,
  onboardingUseCases: [],
  onboardingIntent: null,
};

export const APP_STORAGE_VERSION = 2;

export const appStorageName = (platform: string): string =>
  `1transcribe-${platform}-user-storage`;

const persistedKeys = [
  "userToken",
  "deviceId",
  "loginPromptDismissed",
  "globalDictationOnboardingDismissed",
  "globalDictationIntroCompleted",
  "globalDictationAccessibilityDisclosureAccepted",
  "selectedTagFilter",
  "webSubscriptionPlans",
  "country",
  "guestTranscript",
  "micPermissionGranted",
  "notificationPermissionRequested",
  "showLoginBanner",
  "isSubscribed",
  "remoteConfig",
  "userData",
  "hasCompletedOnboarding",
  "ratedTranscriptIds",
  "postTranscriptionReviewRequested",
  "firstLaunchAt",
  "dictationBubbleOpenedAt",
  "lastAppOpenAt",
  "lastTranscriptCreatedAt",
  "featureFirstUsedAt",
  "onboardingPersona",
  "onboardingUseCases",
  "onboardingIntent",
  "dictationVocabulary",
  "lastAiInstructions",
  "transcripts",
] as const satisfies readonly (keyof AppState)[];

// Offline continuity: cache one list page of the newest transcripts so home
// renders when the network is down. Bounded because segment-heavy
// transcripts are large and storage backends have size limits.
const PERSISTED_TRANSCRIPTS_CAP = 10;

export type PersistedAppState = Partial<
  Pick<AppState, (typeof persistedKeys)[number]>
>;

const _isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const selectPersistedState = (state: AppState): PersistedAppState => {
  const persisted: Record<string, unknown> = {};
  for (const key of persistedKeys) {
    if (state[key] !== undefined) persisted[key] = state[key];
  }
  persisted.transcripts = state.transcripts.slice(0, PERSISTED_TRANSCRIPTS_CAP);
  return persisted as PersistedAppState;
};

export const migratePersistedState = (
  persisted: unknown,
  fromVersion: number,
): PersistedAppState => {
  if (![0, 1, APP_STORAGE_VERSION].includes(fromVersion)) {
    throw new Error(`Unsupported app storage version: ${fromVersion}`);
  }
  if (!_isRecord(persisted)) {
    throw new Error("Persisted app state must be an object");
  }

  // Version 0 used an exclusion list. Versions 0-1 also carried the retired
  // feature-discovery notification campaign fields. Copy only data that is
  // deliberately durable in version 2; unknown and retired fields are dropped.
  const migrated: Record<string, unknown> = {};
  for (const key of persistedKeys) {
    if (persisted[key] !== undefined) migrated[key] = persisted[key];
  }
  return migrated as PersistedAppState;
};

export const expiredSessionState = (
  state: AppState,
  expiredAt: number,
): Partial<AppState> | undefined => {
  if (!state.userToken || state._sessionExpiredAt !== null) return undefined;
  return {
    userToken: undefined,
    userData: undefined,
    isSubscribed: false,
    subscriptionManagementProvider: undefined,
    transcripts: [],
    transcriptsNextCursor: undefined,
    _sessionExpiredAt: expiredAt,
  };
};

export type RawStateStorage = {
  getItem: (name: string) => string | null | Promise<string | null>;
  setItem: (name: string, value: string) => void | Promise<void>;
  removeItem: (name: string) => void | Promise<void>;
};

export type HydrationWriteStatus = "pending" | "ready" | "failed";

// Zustand persists on every set, including sets that happen before or after a
// failed hydration. This guard prevents an unreadable production value from
// being silently replaced by defaults. A successful hydration explicitly
// enables writes; a failed one remains read-only until restart/recovery.
export const createHydrationWriteGuard = (raw: RawStateStorage) => {
  let status: HydrationWriteStatus = "pending";

  const storage: RawStateStorage = {
    getItem: (name) => raw.getItem(name),
    setItem: (name, value) => {
      if (status !== "ready") return;
      return raw.setItem(name, value);
    },
    removeItem: (name) => {
      if (status !== "ready") return;
      return raw.removeItem(name);
    },
  };

  return {
    storage,
    status: (): HydrationWriteStatus => status,
    beginHydration: () => {
      status = "pending";
    },
    markReady: () => {
      if (status === "pending") status = "ready";
    },
    markFailed: () => {
      status = "failed";
    },
  };
};
export type AppStoreActions = {
  setUserToken: (token: string) => void;
  setLoginPromptDismissed: (loginPromptDismissed: boolean) => void;
  setGlobalDictationOnboardingDismissed: (
    globalDictationOnboardingDismissed: boolean,
  ) => void;
  setGlobalDictationAccessibilityDisclosureAccepted: (
    globalDictationAccessibilityDisclosureAccepted: boolean,
  ) => void;
  setHasCompletedOnboarding: (hasCompletedOnboarding: boolean) => void;
  setSelectedTagFilter: (selectedTagFilter: string | null) => void;
  setShowLoginBanner: (showLoginBanner: boolean) => void;
  markTranscriptAsRated: (id: string) => void;
  setHasHydrated: (status: boolean) => void;
  expireSession: () => void;
  setIsSubscribed: (isSubscribed: boolean) => void;
  setWebSubscriptionPlans: (plans: AppState["webSubscriptionPlans"]) => void;
  setDeviceId: (deviceId: string) => void;
  setCountry: (country: string) => void;
  setMicPermissionGranted: (micPermissionGranted: boolean) => void;
  setNotificationPermissionRequested: (
    notificationPermissionRequested: boolean,
  ) => void;
  setGuestTranscript: (guestTranscript: Transcript) => void;
  // ─── Lifecycle ───
  recordFirstLaunch: () => void;
  recordDictationBubbleOpened: (at: number) => void;
  completeGlobalDictationIntro: () => void;
  recordAppOpen: () => void;
  recordTranscriptCreated: () => void;
  recordFeatureFirstUsed: (feature: FeatureKey) => void;
  // ─── Onboarding identity ───
  setOnboardingPersona: (persona: Persona | null) => void;
  setOnboardingUseCases: (useCases: string[]) => void;
  setOnboardingIntent: (intent: OnboardingIntent | null) => void;
  setDictationVocabulary: (vocabulary: AppState["dictationVocabulary"]) => void;
  setLastAiInstructions: (
    feature: "summary" | "quiz" | "flashcard",
    instructions: string,
  ) => void;
  reset: () => void;
};

export type AppStoreDependencies = {
  platform: "web" | "ios" | "android";
  storage: RawStateStorage;
};

export const createAppStore = ({ platform, storage }: AppStoreDependencies) => {
  const storageGuard = createHydrationWriteGuard(storage);

  const useStore = create<AppState & AppStoreActions>()(
    persist<AppState & AppStoreActions, [], [], PersistedAppState>(
      (set, get) => ({
        ...initialState,
        setCountry: (country) => {
          set({ country });
        },
        setMicPermissionGranted: (micPermissionGranted) => {
          set({ micPermissionGranted });
        },
        setNotificationPermissionRequested: (
          notificationPermissionRequested,
        ) => {
          set({ notificationPermissionRequested });
        },
        recordFirstLaunch: () => {
          if (get().firstLaunchAt) return;
          set({ firstLaunchAt: Date.now() });
        },
        recordDictationBubbleOpened: (dictationBubbleOpenedAt) => {
          if (get().dictationBubbleOpenedAt) return;
          set({ dictationBubbleOpenedAt });
        },
        completeGlobalDictationIntro: () => {
          if (get().globalDictationIntroCompleted) return;
          set({ globalDictationIntroCompleted: true });
        },
        recordAppOpen: () => {
          set({ lastAppOpenAt: Date.now() });
        },
        recordTranscriptCreated: () => {
          const now = Date.now();
          const featureFirstUsedAt = get().featureFirstUsedAt || {};
          set({
            lastTranscriptCreatedAt: now,
            featureFirstUsedAt: {
              ...featureFirstUsedAt,
              transcribe: featureFirstUsedAt.transcribe ?? now,
            },
          });
        },
        recordFeatureFirstUsed: (feature) => {
          const featureFirstUsedAt = get().featureFirstUsedAt || {};
          if (featureFirstUsedAt[feature]) return;
          set({
            featureFirstUsedAt: {
              ...featureFirstUsedAt,
              [feature]: Date.now(),
            },
          });
        },
        setOnboardingPersona: (onboardingPersona) => {
          set({ onboardingPersona });
        },
        setOnboardingIntent: (onboardingIntent) => {
          set({ onboardingIntent });
        },
        setOnboardingUseCases: (onboardingUseCases) => {
          set({ onboardingUseCases });
        },
        setDictationVocabulary: (dictationVocabulary) => {
          set({ dictationVocabulary });
        },
        setLastAiInstructions: (feature, instructions) => {
          set((state) => ({
            lastAiInstructions: {
              ...state.lastAiInstructions,
              [feature]: instructions,
            },
          }));
        },
        setLoginPromptDismissed: (loginPromptDismissed) => {
          set({ loginPromptDismissed });
        },
        setGlobalDictationOnboardingDismissed: (
          globalDictationOnboardingDismissed,
        ) => {
          set({ globalDictationOnboardingDismissed });
        },
        setGlobalDictationAccessibilityDisclosureAccepted: (
          globalDictationAccessibilityDisclosureAccepted,
        ) => {
          set({ globalDictationAccessibilityDisclosureAccepted });
        },
        setHasCompletedOnboarding: (hasCompletedOnboarding) => {
          set({ hasCompletedOnboarding });
        },
        setSelectedTagFilter: (selectedTagFilter) => {
          set({ selectedTagFilter });
        },
        setShowLoginBanner: (showLoginBanner) => {
          set({ showLoginBanner });
        },
        markTranscriptAsRated: (id) => {
          const { ratedTranscriptIds } = get();
          if (!ratedTranscriptIds.includes(id)) {
            const updated = [...ratedTranscriptIds, id];
            set({ ratedTranscriptIds: updated.slice(-100) });
          }
        },
        setWebSubscriptionPlans: (webSubscriptionPlans) => {
          set({
            webSubscriptionPlans:
              normalizeWebSubscriptionPlans(webSubscriptionPlans),
          });
        },
        setGuestTranscript: (guestTranscript) => {
          set({ guestTranscript });
        },
        setHasHydrated: (status) => {
          set({ _hasHydrated: status });
        },
        setUserToken: (token) => {
          set({ userToken: token, _sessionExpiredAt: null });
        },
        expireSession: () => {
          const expired = expiredSessionState(get(), Date.now());
          if (expired) set(expired);
        },
        setIsSubscribed: (isSubscribed) => {
          set({ isSubscribed });
        },
        setDeviceId: (deviceId) => {
          set({ deviceId });
        },
        reset: () => {
          set({
            ...get(),
            userToken: undefined,
            userData: undefined,
            isSubscribed: false,
            subscriptionManagementProvider: undefined,
            transcripts: [],
            transcriptsNextCursor: undefined,
            _sessionExpiredAt: null,
          });
        },
      }),
      {
        name: appStorageName(platform),
        storage: createJSONStorage(() => storageGuard.storage),
        version: APP_STORAGE_VERSION,
        migrate: migratePersistedState,
        skipHydration: true,
        onRehydrateStorage: () => (_state, error) => {
          if (error) {
            storageGuard.markFailed();
            console.warn("Zustand rehydration failed");
            return;
          }

          storageGuard.markReady();
        },
        partialize: selectPersistedState,
      },
    ),
  );

  const hydrate = async (): Promise<boolean> => {
    storageGuard.beginHydration();
    useStore.setState({ _hasHydrated: false, _hydrationError: false });
    await useStore.persist.rehydrate();
    if (storageGuard.status() !== "ready") {
      useStore.setState({ _hasHydrated: false, _hydrationError: true });
      return false;
    }
    try {
      await useStore.setState({
        _hasHydrated: true,
        _hydrationError: false,
        transcripts: useStore.getState().transcripts || [],
      });
      return true;
    } catch {
      storageGuard.markFailed();
      useStore.setState({ _hasHydrated: false, _hydrationError: true });
      console.warn("Zustand post-hydration persistence failed");
      return false;
    }
  };

  return { hydrate, useStore };
};

export type AppStore = ReturnType<typeof createAppStore>;
export type AppStoreHook = AppStore["useStore"];
